import * as React from 'react';
import { IProcessAktivitetFlode } from '../../../../Types/ProcessModel';
import EDStepper from '../../../../BaseComponents/Stepper/EDStepper';
import EDStep from '../../../../BaseComponents/Stepper/Components/EDStep';
import EDStepButton from '../../../../BaseComponents/Stepper/Components/EDStepButton';
import Activity from '../Activity/Activity';
import EDStepLabel from '../../../../BaseComponents/Stepper/Components/EDStepLabel';
import EDGrid from '../../../../BaseComponents/GridComponents/EDGrid';
import EDForwardChevron from '../../../../BaseComponents/Buttons/SpecificButtons/ForwardChevron/EDForwardChevron';
import EDBackChevron from '../../../../BaseComponents/Buttons/SpecificButtons/BackChevron/EDBackChevron';
import EDTypography from '../../../../BaseComponents/Typography/EDTypography';
import EDPaper from '../../../../BaseComponents/Paper/EDPaper';
import EDIcon, { IconColor } from '../../../../BaseComponents/Icon/EDIcon';
import ProcessWarningOverlay from '../ProcessWarningOverlay/ProcessWarningOverlay';
import { IProcessWarning } from '../ProcessHeaderBase/ProcessHeaderBase';
import { Theme } from '@mui/material/styles';
import { withStyles } from 'tss-react/mui';
import EDOverlay from '../../../../BaseComponents/Overlay/EDOverlay';
import EDTooltip from '../../../../BaseComponents/Tooltip/EDTooltip';
import { IconType } from '../../../../BaseComponents/Icon/Utils/IconDefinitions';

interface IProps {
  activityFlow: IProcessAktivitetFlode;
  warnings?: IProcessWarning[];
  classes?: any;
}

interface IState {
  activeStep: number;
  activeTab: number;
}

const styles = (theme: Theme) => ({
  activityContainer: {
    maxWidth: '700px',
  },
  overlayPosition: {
    bottom: -14,
    right: -14,
  },
  navLabel: {
    paddingLeft: '16px',
    paddingRight: '16px',
  },
  navOverlayPosition: {
    bottom: -14,
    right: -8,
  },
});

const Nav = (props: {
  hasContent: boolean;
  label: string;
  labelClassName: string;
  onButtonClick: () => void;
  reverse?: boolean;
  warning?: string;
  warningClassName: string;
}) => {
  const chevron = props.reverse ? (
    <EDForwardChevron
      disabled={!props.hasContent}
      onClick={props.onButtonClick}
      tooltip="Nästa"
    />
  ) : (
    <EDBackChevron
      disabled={!props.hasContent}
      onClick={props.onButtonClick}
      tooltip="Bakåt"
    />
  );
  return (
    <EDGrid
      item
      container
      xs={2}
      direction={props.reverse ? 'row-reverse' : 'row'}
      justifyContent="flex-end"
      alignItems="center"
      wrap="nowrap"
    >
      {props.hasContent && (
        <React.Fragment>
          <EDGrid item>
            <EDOverlay
              className={props.labelClassName}
              overlay={props.warning && <ProcessWarningOverlay />}
              classes={{ overlay: props.warningClassName }}
            >
              <EDTooltip title={props.warning}>
                <EDTypography
                  style={{ cursor: props.hasContent ? 'pointer' : 'default' }}
                  onClick={props.hasContent ? props.onButtonClick : undefined}
                  variant="body2"
                >
                  {props.label}
                </EDTypography>
              </EDTooltip>{' '}
            </EDOverlay>
          </EDGrid>
          <EDGrid item>{chevron}</EDGrid>
        </React.Fragment>
      )}
    </EDGrid>
  );
};

/**
 * En komponent som ger användaren navigering mellan flera olika aktiviter som i sin tur visas genom Activity komponenenten.
 */
export class ActivityFlowPage extends React.PureComponent<IProps, IState> {
  state: IState = {
    activeStep: 1,
    activeTab: 0,
  };

  /*
   * TODO: refactor enligt https://reactjs.org/blog/2018/03/27/update-on-async-rendering.html
   *       där componentWillReceiveProps, componentWillMount och componentWillUpdate är märkta som obsoleta.
   *       UNSAFE_fungerar som migrationsverktyg, men är inte en permanent löstning.
   */
  UNSAFE_componentWillUpdate(prevProps: IProps) {
    if (this.props.activityFlow !== prevProps.activityFlow) {
      this.setState({ activeStep: 1 });
      this.forceUpdate();
    }
  }

  handleNext = () => {
    this.setState(state => ({
      activeStep:
        state.activeStep + 1 > this.props.activityFlow.Aktiviteter.length - 1
          ? 0
          : state.activeStep + 1,
    }));
  };

  handleBack = () => {
    this.setState(state => ({
      activeStep: state.activeStep - 1,
    }));
  };

  handleStep = (step: number) => {
    this.setState({ activeStep: step });
  };

  hasNext = () => {
    return (
      this.state.activeStep < this.props.activityFlow.Aktiviteter.length - 1
    );
  };

  hasPrev = () => {
    return (
      this.state.activeStep !== 0 &&
      this.state.activeStep - 1 < this.props.activityFlow.Aktiviteter.length
    );
  };

  prevActivityRubrik = (): string => {
    if (this.hasPrev()) {
      return this.props.activityFlow.Aktiviteter[this.state.activeStep - 1]
        .Rubrik;
    } else {
      return '';
    }
  };

  nextActivityRubrik = (): string => {
    const next: number =
      this.state.activeStep < this.props.activityFlow.Aktiviteter.length - 1
        ? this.state.activeStep + 1
        : 0;

    return this.props.activityFlow.Aktiviteter[next].Rubrik || '';
  };

  handleTabChange = (tab: number) => {
    this.setState({ activeTab: tab });
  };

  checkWarningMessage = (aktIndex: number, warnings?: IProcessWarning[]) => {
    const warning = warnings && warnings.find(w => w.activity === aktIndex);
    return warning && warning.message;
  };

  render() {
    // När vi byter från ett activityFlow till ett annat kan activeStep > antal aktiviteter i det nya flowet
    // Ordnas när vi kör setState() i componentWillUpdate, men vi får 1 render ändå
    if (this.state.activeStep >= this.props.activityFlow.Aktiviteter.length) {
      return 'Loading';
    }
    let prevWarning = this.checkWarningMessage(
      this.state.activeStep - 1,
      this.props.warnings
    );
    let nextWarning = this.checkWarningMessage(
      this.state.activeStep + 1,
      this.props.warnings
    );

    return (
      <React.Fragment>
        <EDStepper
          activeStep={this.state.activeStep}
          orientation="horizontal"
          nonLinear
          alternativeLabel
          style={{ overflow: 'auto' }}
        >
          {this.props.activityFlow.Aktiviteter.map((x, index) => {
            const isActiveStep = index === this.state.activeStep;
            let color: IconColor | undefined = isActiveStep
              ? 'primary'
              : 'action';
            let warning = this.checkWarningMessage(index, this.props.warnings);
            let iconType: IconType = x.Aveny
              ? x.Automatisk
                ? 'process_automatic_step'
                : 'process_manual_step'
              : 'process_external_inert';
            return (
              <EDStep key={x.ID} style={{ minWidth: 112 }}>
                <EDStepButton
                  onClick={() => this.handleStep(index)}
                  title={warning}
                  icon={
                    <EDOverlay
                      overlay={warning && <ProcessWarningOverlay />}
                      classes={{ overlay: this.props.classes.overlayPosition }}
                    >
                      <EDIcon iconType={iconType} color={color} />
                    </EDOverlay>
                  }
                >
                  <EDStepLabel
                    onClick={() => this.handleStep(index)}
                    StepIconProps={{ completed: isActiveStep }}
                  >
                    {x.Rubrik}
                  </EDStepLabel>
                </EDStepButton>
              </EDStep>
            );
          })}
        </EDStepper>

        {this.props.activityFlow && (
          <EDPaper>
            <EDGrid
              container
              justifyContent="center"
              alignItems="stretch"
              style={{ width: '100%' }}
            >
              {/* Back navigation */}
              <Nav
                hasContent={this.hasPrev()}
                label={this.prevActivityRubrik()}
                labelClassName={this.props.classes.navLabel}
                onButtonClick={this.handleBack}
                warning={prevWarning}
                warningClassName={this.props.classes.navOverlayPosition}
              />
              <EDGrid
                item
                container
                xs={6}
                justifyContent="center"
                className={this.props.classes.activityContainer}
              >
                {/* Main Content */}
                <Activity
                  activity={
                    this.props.activityFlow.Aktiviteter[this.state.activeStep]
                  }
                  showTabs={
                    this.props.activityFlow.Aktiviteter[
                      this.state.activeStep
                    ] &&
                    this.props.activityFlow.Aktiviteter[this.state.activeStep]
                      .Aveny
                  }
                  activeTab={this.state.activeTab}
                  onTabChange={this.handleTabChange}
                />
              </EDGrid>
              {/* Forward navigation */}
              <Nav
                hasContent={this.hasNext()}
                label={this.nextActivityRubrik()}
                labelClassName={this.props.classes.navLabel}
                onButtonClick={this.handleNext}
                reverse
                warning={nextWarning}
                warningClassName={this.props.classes.navOverlayPosition}
              />
            </EDGrid>
          </EDPaper>
        )}
      </React.Fragment>
    );
  }
}

export default withStyles(ActivityFlowPage, styles);
